import { BLEND_SEED_LENGTH } from "../scoring";
import type { DailyPuzzleResult, DailyPuzzleSource } from "./daily-puzzle-source";
import { derivePuzzleFromId } from "./derive-puzzle";
import type { DailyPuzzle } from "./types";

/**
 * `DailyPuzzleSource` that derives today's puzzle locally from the UTC
 * calendar date, with no network dependency. The original v1 behavior:
 * two domain-separated SHA-256 hashes of `YYYY-MM-DD` produce the
 * blend and scorer seed.
 *
 * Useful for:
 *
 *   - Local dev without `CogQuest.Service` running.
 *   - Demos and previews where the on-chain anchor isn't meaningful.
 *   - E2E and screenshot setups that don't want a backend dependency.
 *
 * Always resolves successfully — there's no "pending" or "unavailable"
 * state for a deterministic local derivation. The chain-anchored
 * fields on {@link DailyPuzzle} (`miningInputBlockHash`, `domainId`)
 * are filled with inert sentinel values: no chain → no real hash, and
 * the SPA doesn't render those fields anywhere.
 */

/// All-zero hex placeholder for `miningInputBlockHash`. Sized to match
/// the API's lowercase-hex contract.
const LOCAL_MINING_INPUT_BLOCK_HASH = "0".repeat(BLEND_SEED_LENGTH * 2);

/// Sentinel domain id — local mode is not bound to any cogcoin domain.
const LOCAL_DOMAIN_ID = 0;

export class LocalDailyPuzzleSource implements DailyPuzzleSource {
  constructor(private readonly now: () => Date = () => new Date()) {}

  async fetchTodaysPuzzle(): Promise<DailyPuzzleResult> {
    const id = utcDateId(this.now());
    const base = await derivePuzzleFromId(id);
    const puzzle: DailyPuzzle = {
      id: base.id,
      blend: base.blend,
      blendIndices: base.blendIndices,
      blendSeed: base.blendSeed,
      resolvedFor: id,
      miningInputBlockHash: LOCAL_MINING_INPUT_BLOCK_HASH,
      domainId: LOCAL_DOMAIN_ID,
    };
    return { status: "ok", puzzle, stale: false };
  }
}

function utcDateId(d: Date): string {
  const yyyy = String(d.getUTCFullYear()).padStart(4, "0");
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}
